import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Inbox, Users, Sparkles, Check, TriangleAlert, ChevronDown, MapPin, Info } from "lucide-react";

const DIM_LABELS = { skills: "Zručnosti", experience: "Skúsenosti", seniority: "Seniorita", location: "Lokalita", work_mode: "Pracovný režim" };
const DIM_ORDER = ["skills", "experience", "seniority", "location", "work_mode"];

function scoreColor(s) { return s >= 70 ? "text-emerald-600" : s >= 40 ? "text-amber-500" : "text-slate-400"; }
function barColor(v) { return v >= 70 ? "bg-emerald-500" : v >= 40 ? "bg-amber-500" : "bg-slate-400"; }

function CandidateRow({ r, jobId, showBadge }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`applicant-${r.id}`} className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5">
      <div className="flex items-center gap-3">
        <button onClick={() => nav(`/company/candidate/${r.id}?job_id=${jobId}`)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
          {r.photo_url ? <img src={r.photo_url} alt={r.name} className="h-11 w-11 rounded-full object-cover" /> : <div className="h-11 w-11 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-400">{(r.name || "?").charAt(0)}</div>}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="font-head font-semibold truncate">{r.name || "Kandidát"}</div>
              {showBadge && r.new_reaction && (
                <span data-testid={`new-reaction-badge-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold text-white"><Sparkles size={10} /> Nová reakcia</span>
              )}
            </div>
            <div className="text-sm text-slate-500 truncate">{r.title}{r.location ? <span className="inline-flex items-center gap-1 ml-2 text-slate-400"><MapPin size={11} /> {r.location}</span> : null}</div>
          </div>
        </button>
        <div className="text-right"><div className={`font-head text-2xl font-bold ${scoreColor(r.score)}`}>{r.score}%</div><div className="text-[10px] text-slate-400 uppercase">zhoda</div></div>
      </div>

      <div className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1">
        {(r.strengths || []).slice(0, 4).map((s, j) => <div key={j} className="flex gap-1.5 text-sm text-slate-700"><Check size={15} className="text-emerald-500 flex-shrink-0 mt-0.5" />{s}</div>)}
        {(r.skill_gaps || []).slice(0, 3).map((s, j) => <div key={j} className="flex gap-1.5 text-sm text-slate-500"><TriangleAlert size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />Chýba: {s}</div>)}
      </div>

      <button data-testid={`applicant-why-${r.id}`} onClick={() => setOpen(!open)} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:underline">
        Detail zhody <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 rounded-2xl bg-slate-50 border border-slate-100 p-4 space-y-2" data-testid={`applicant-detail-${r.id}`}>
          {DIM_ORDER.filter((k) => typeof (r.breakdown || {})[k] === "number").map((k) => (
            <div key={k}>
              <div className="flex justify-between text-xs mb-0.5"><span className="text-slate-600 font-medium">{DIM_LABELS[k]}</span><span className="text-slate-400">{r.breakdown[k]}%</span></div>
              <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className={`h-full rounded-full ${barColor(r.breakdown[k])}`} style={{ width: `${r.breakdown[k]}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function JobApplicants({ jobId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/company/jobs/${jobId}/applicants`).then((r) => setData(r.data)).catch(() => setData({ applicants: [], db_candidates: [] }));
  }, [jobId]);

  return (
    <div className="mt-8 max-w-2xl" data-testid="job-applicants">
      <h2 className="font-head text-xl font-semibold flex items-center gap-2"><Inbox size={18} className="text-blue-700" /> Uchádzači o pozíciu</h2>
      <p className="mt-2 text-sm text-slate-500">Kandidáti, ktorí na túto ponuku reagovali cez Profeeluj, zoradení podľa zhody s ponukou.</p>

      {data === null ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-blue-700" /></div>
      ) : data.applicants.length === 0 ? (
        <p className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-sm text-slate-500 text-center" data-testid="applicants-empty">Zatiaľ na túto ponuku nikto nereagoval.</p>
      ) : (
        <div className="mt-4 space-y-3" data-testid="applicants-list">
          {data.applicants.map((r) => <CandidateRow key={r.id} r={r} jobId={jobId} showBadge />)}
        </div>
      )}

      {data && data.db_candidates?.length > 0 && (
        <>
          <h2 className="mt-10 font-head text-xl font-semibold flex items-center gap-2"><Users size={18} className="text-blue-700" /> Ďalší kandidáti z databázy</h2>
          <p className="mt-2 text-sm text-slate-500">Vhodní kandidáti z vašej databázy, ktorí zatiaľ nereagovali, zoradení podľa zhody.</p>
          <div className="mt-3 flex items-start gap-2 rounded-2xl bg-blue-50 border border-blue-100 p-3 text-xs text-blue-800"><Info size={15} className="flex-shrink-0 mt-0.5" /> {data.disclaimer || "Skóre zhody je odporúčanie."}</div>
          <div className="mt-4 space-y-3" data-testid="db-candidates-list">
            {data.db_candidates.map((r) => <CandidateRow key={r.id} r={r} jobId={jobId} />)}
          </div>
        </>
      )}
    </div>
  );
}
